import { Link } from "react-router-dom";
import { ArrowRight, Ship, FileCheck2, Plane, Warehouse, Truck, ShieldCheck } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import QuoteForm from "../components/QuoteForm";
import LogisticsCalculator from "../components/LogisticsCalculator";

const HERO = "https://images.pexels.com/photos/12530455/pexels-photo-12530455.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const icons = [Ship, FileCheck2, Plane, Warehouse, Truck, ShieldCheck];

export default function Logistics() {
  const { t } = useLang();

  return (
    <div data-testid="logistics-page">
      {/* Hero */}
      <section className="relative bg-[#0B1B29] text-white overflow-hidden">
        <img
          src={HERO}
          alt="Container ship at sea"
          className="absolute inset-0 w-full h-full object-cover opacity-45"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative pgb-container py-24 md:py-32 max-w-4xl">
          <span className="eyebrow text-[#93C5FD]">{t.logisticsPage.eyebrow}</span>
          <h1 className="mt-5 text-4xl md:text-6xl font-black tracking-tighter leading-[1.02]">
            {t.logisticsPage.title}
          </h1>
          <p className="mt-6 text-white/75 max-w-2xl leading-relaxed">
            {t.logisticsPage.subtitle}
          </p>
          <Link to="/quote" className="btn-accent mt-10" data-testid="logistics-hero-cta">
            {t.common.requestQuote} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 md:py-32 bg-white">
        <div className="pgb-container">
          <div className="mb-12 max-w-2xl">
            <span className="eyebrow">{t.logisticsPage.servicesTitle}</span>
            <h2 className="mt-3 text-3xl md:text-5xl font-black tracking-tighter text-[#0B1B29]">
              End-to-end. Transparent. On time.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {t.logisticsPage.services.map((s, idx) => {
              const Icon = icons[idx % icons.length];
              return (
                <div
                  key={s.title}
                  className="bg-white p-8 hover:bg-[#F5F5F7] transition-colors"
                  data-testid={`logistics-service-${idx}`}
                >
                  <Icon size={22} className="text-[#1D4ED8]" />
                  <h3 className="mt-4 text-lg font-bold tracking-tight text-[#0B1B29]">{s.title}</h3>
                  <p className="mt-2 text-sm text-[#536878] leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Routes table */}
      <section className="py-24 md:py-32 bg-[#0B1B29] text-white">
        <div className="pgb-container">
          <div className="mb-10">
            <span className="eyebrow text-[#93C5FD]">{t.logisticsPage.routesTitle}</span>
            <h2 className="mt-3 text-3xl md:text-5xl font-black tracking-tighter">
              China ↔ Baku
            </h2>
          </div>

          <div className="border border-white/15">
            <div className="grid grid-cols-12 px-6 py-4 text-xs font-semibold tracking-[0.2em] uppercase text-white/50 border-b border-white/15">
              <div className="col-span-4">From</div>
              <div className="col-span-4">To</div>
              <div className="col-span-2">Mode</div>
              <div className="col-span-2 text-right">Transit</div>
            </div>
            {t.logisticsPage.routes.map((r, idx) => (
              <div
                key={`${r.from}-${r.to}-${r.mode}`}
                className="grid grid-cols-12 px-6 py-5 items-center border-b border-white/10 last:border-b-0 hover:bg-white/5 transition-colors"
                data-testid={`logistics-route-${idx}`}
              >
                <div className="col-span-4 font-medium">{r.from}</div>
                <div className="col-span-4 text-white/75">{r.to}</div>
                <div className="col-span-2 text-white/85 text-sm">{r.mode}</div>
                <div className="col-span-2 text-right font-mono text-xs tracking-wider text-[#93C5FD]">{r.days}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Calculator */}
      <LogisticsCalculator />

      {/* Form */}
      <section className="py-24 md:py-32 bg-[#F5F5F7]">
        <div className="pgb-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="eyebrow">{t.common.requestQuote}</span>
            <h2 className="mt-4 text-3xl md:text-5xl font-black tracking-tighter text-[#0B1B29]">
              {t.quotePage.title}
            </h2>
            <p className="mt-5 text-[#536878] leading-relaxed max-w-md">
              {t.quotePage.subtitle}
            </p>
          </div>
          <div className="lg:col-span-7">
            <QuoteForm defaultProduct="logistics" />
          </div>
        </div>
      </section>
    </div>
  );
}
